import {
  Component,
  Input,
  ElementRef,
  HostListener,
  inject,
  Output,
  EventEmitter
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AngleDownComponent } from '../icons/angle-down';
import { LocaleService } from 'src/app/services/locale';

@Component({
  selector: 'app-country-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, AngleDownComponent],
  template: `
    <div class="relative w-full">
      <!-- Trigger -->
      <div
        (click)="toggleDropdown()"
        tabindex="0"
        class="relative flex items-center px-2 py-1.5 sm:py-2 pr-6 text-[12px] rounded border w-full cursor-pointer select-none transition-colors bg-white"
        [ngClass]="dropdownOpen ? 'border-[#043B22]' : 'border-[#D1D5DB] hover:border-[#9CA3AF]'"
      >
        <span class="truncate" [ngClass]="selectedLabel ? 'text-[#212121]' : 'text-[#9CA3AF]'">
          {{ selectedLabel || strings[language].select_country }}
        </span>
        <span
          class="text-[#616161] absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200"
          [ngClass]="dropdownOpen ? 'rotate-180' : ''"
        >
          <app-angle-down></app-angle-down>
        </span>
      </div>

      <!-- Dropdown panel -->
      <div
        *ngIf="dropdownOpen"
        class="absolute top-full left-0 right-0 mt-1 bg-white border border-[#D1D5DB] rounded-lg shadow-lg z-50 overflow-hidden"
      >
        <!-- Search -->
        <div class="p-2 border-b border-[#F3F4F6]">
          <input
            type="text"
            class="w-full px-2 py-1.5 text-[12px] rounded border border-[#D1D5DB] focus:outline-none focus:border-[#043B22] transition-colors placeholder:text-[#9CA3AF]"
            [(ngModel)]="searchText"
            (ngModelChange)="filterCountries()"
            [placeholder]="language === 'english' ? 'Search country' : 'Cerca paese'"
            (click)="$event.stopPropagation()"
          />
        </div>

        <!-- List -->
        <ul class="max-h-52 overflow-y-auto">
          <li
            *ngFor="let country of filteredCountries"
            (click)="selectCountry(country)"
            class="px-3 py-2 text-[12px] text-[#212121] cursor-pointer hover:bg-[#043B22]/10 hover:text-[#043B22] transition-colors border-b border-[#F9FAFB] last:border-0"
          >
            {{ country.name }}
            <span class="text-[#9CA3AF] ml-1">({{ country.locode }})</span>
          </li>
          <li *ngIf="filteredCountries.length === 0" class="px-3 py-3 text-[12px] text-[#9CA3AF] text-center">
            {{ language === 'english' ? 'No countries found' : 'Nessun paese trovato' }}
          </li>
        </ul>
      </div>
    </div>
  `,
  styles: []
})
export class CountryPickerComponent {
  @Input() control: FormControl | null = null;
  @Output() change = new EventEmitter<string>();
  language = inject(LocaleService).getLanguage();
  countries = [
    // { name: 'Global', locode: 'global' },
    { name: 'Afghanistan', locode: 'AF' },
    { name: 'Albania', locode: 'AL' },
    { name: 'Algeria', locode: 'DZ' },
    { name: 'Andorra', locode: 'AD' },
    { name: 'Angola', locode: 'AO' },
    { name: 'Antigua and Barbuda', locode: 'AG' },
    { name: 'Argentina', locode: 'AR' },
    { name: 'Armenia', locode: 'AM' },
    { name: 'Australia', locode: 'AU' },
    { name: 'Austria', locode: 'AT' },
    { name: 'Azerbaijan', locode: 'AZ' },
    { name: 'Bahamas', locode: 'BS' },
    { name: 'Bahrain', locode: 'BH' },
    { name: 'Bangladesh', locode: 'BD' },
    { name: 'Barbados', locode: 'BB' },
    { name: 'Belarus', locode: 'BY' },
    { name: 'Belgium', locode: 'BE' },
    { name: 'Belize', locode: 'BZ' },
    { name: 'Benin', locode: 'BJ' },
    { name: 'Bhutan', locode: 'BT' },
    { name: 'Bolivia', locode: 'BO' },
    { name: 'Bosnia and Herzegovina', locode: 'BA' },
    { name: 'Botswana', locode: 'BW' },
    { name: 'Brazil', locode: 'BR' },
    { name: 'Brunei', locode: 'BN' },
    { name: 'Bulgaria', locode: 'BG' },
    { name: 'Burkina Faso', locode: 'BF' },
    { name: 'Burundi', locode: 'BI' },
    { name: 'Cabo Verde', locode: 'CV' },
    { name: 'Cambodia', locode: 'KH' },
    { name: 'Cameroon', locode: 'CM' },
    { name: 'Canada', locode: 'CA' },
    { name: 'Central African Republic', locode: 'CF' },
    { name: 'Chad', locode: 'TD' },
    { name: 'Chile', locode: 'CL' },
    { name: 'China', locode: 'CN' },
    { name: 'Colombia', locode: 'CO' },
    { name: 'Comoros', locode: 'KM' },
    { name: 'Congo (Congo-Brazzaville)', locode: 'CG' },
    { name: 'Costa Rica', locode: 'CR' },
    { name: 'Croatia', locode: 'HR' },
    { name: 'Cuba', locode: 'CU' },
    { name: 'Cyprus', locode: 'CY' },
    { name: 'Czech Republic', locode: 'CZ' },
    { name: 'Democratic Republic of the Congo', locode: 'CD' },
    { name: 'Denmark', locode: 'DK' },
    { name: 'Djibouti', locode: 'DJ' },
    { name: 'Dominica', locode: 'DM' },
    { name: 'Dominican Republic', locode: 'DO' },
    { name: 'Ecuador', locode: 'EC' },
    { name: 'Egypt', locode: 'EG' },
    { name: 'El Salvador', locode: 'SV' },
    { name: 'Equatorial Guinea', locode: 'GQ' },
    { name: 'Eritrea', locode: 'ER' },
    { name: 'Estonia', locode: 'EE' },
    { name: 'Eswatini', locode: 'SZ' },
    { name: 'Ethiopia', locode: 'ET' },
    { name: 'Fiji', locode: 'FJ' },
    { name: 'Finland', locode: 'FI' },
    { name: 'France', locode: 'FR' },
    { name: 'Gabon', locode: 'GA' },
    { name: 'Gambia', locode: 'GM' },
    { name: 'Georgia', locode: 'GE' },
    { name: 'Germany', locode: 'DE' },
    { name: 'Ghana', locode: 'GH' },
    { name: 'Greece', locode: 'GR' },
    { name: 'Grenada', locode: 'GD' },
    { name: 'Guatemala', locode: 'GT' },
    { name: 'Guinea', locode: 'GN' },
    { name: 'Guinea-Bissau', locode: 'GW' },
    { name: 'Guyana', locode: 'GY' },
    { name: 'Haiti', locode: 'HT' },
    { name: 'Honduras', locode: 'HN' },
    { name: 'Hungary', locode: 'HU' },
    { name: 'Iceland', locode: 'IS' },
    { name: 'India', locode: 'IN' },
    { name: 'Indonesia', locode: 'ID' },
    { name: 'Iran', locode: 'IR' },
    { name: 'Iraq', locode: 'IQ' },
    { name: 'Ireland', locode: 'IE' },
    { name: 'Israel', locode: 'IL' },
    { name: 'Italy', locode: 'IT' },
    { name: 'Jamaica', locode: 'JM' },
    { name: 'Japan', locode: 'JP' },
    { name: 'Jordan', locode: 'JO' },
    { name: 'Kazakhstan', locode: 'KZ' },
    { name: 'Kenya', locode: 'KE' },
    { name: 'Kiribati', locode: 'KI' },
    { name: 'Kuwait', locode: 'KW' },
    { name: 'Kyrgyzstan', locode: 'KG' },
    { name: 'Laos', locode: 'LA' },
    { name: 'Latvia', locode: 'LV' },
    { name: 'Lebanon', locode: 'LB' },
    { name: 'Lesotho', locode: 'LS' },
    { name: 'Liberia', locode: 'LR' },
    { name: 'Libya', locode: 'LY' },
    { name: 'Liechtenstein', locode: 'LI' },
    { name: 'Lithuania', locode: 'LT' },
    { name: 'Luxembourg', locode: 'LU' },
    { name: 'Madagascar', locode: 'MG' },
    { name: 'Malawi', locode: 'MW' },
    { name: 'Malaysia', locode: 'MY' },
    { name: 'Maldives', locode: 'MV' },
    { name: 'Mali', locode: 'ML' },
    { name: 'Malta', locode: 'MT' },
    { name: 'Marshall Islands', locode: 'MH' },
    { name: 'Mauritania', locode: 'MR' },
    { name: 'Mauritius', locode: 'MU' },
    { name: 'Mexico', locode: 'MX' },
    { name: 'Micronesia', locode: 'FM' },
    { name: 'Moldova', locode: 'MD' },
    { name: 'Monaco', locode: 'MC' },
    { name: 'Mongolia', locode: 'MN' },
    { name: 'Montenegro', locode: 'ME' },
    { name: 'Morocco', locode: 'MA' },
    { name: 'Mozambique', locode: 'MZ' },
    { name: 'Myanmar (Burma)', locode: 'MM' },
    { name: 'Namibia', locode: 'NA' },
    { name: 'Nauru', locode: 'NR' },
    { name: 'Nepal', locode: 'NP' },
    { name: 'Netherlands', locode: 'NL' },
    { name: 'New Zealand', locode: 'NZ' },
    { name: 'Nicaragua', locode: 'NI' },
    { name: 'Niger', locode: 'NE' },
    { name: 'Nigeria', locode: 'NG' },
    { name: 'North Korea', locode: 'KP' },
    { name: 'North Macedonia', locode: 'MK' },
    { name: 'Norway', locode: 'NO' },
    { name: 'Oman', locode: 'OM' },
    { name: 'Pakistan', locode: 'PK' },
    { name: 'Palau', locode: 'PW' },
    { name: 'Panama', locode: 'PA' },
    { name: 'Papua New Guinea', locode: 'PG' },
    { name: 'Paraguay', locode: 'PY' },
    { name: 'Peru', locode: 'PE' },
    { name: 'Philippines', locode: 'PH' },
    { name: 'Poland', locode: 'PL' },
    { name: 'Portugal', locode: 'PT' },
    { name: 'Qatar', locode: 'QA' },
    { name: 'Romania', locode: 'RO' },
    { name: 'Russia', locode: 'RU' },
    { name: 'Rwanda', locode: 'RW' },
    { name: 'Saint Kitts and Nevis', locode: 'KN' },
    { name: 'Saint Lucia', locode: 'LC' },
    { name: 'Saint Vincent and the Grenadines', locode: 'VC' },
    { name: 'Samoa', locode: 'WS' },
    { name: 'San Marino', locode: 'SM' },
    { name: 'Sao Tome and Principe', locode: 'ST' },
    { name: 'Saudi Arabia', locode: 'SA' },
    { name: 'Senegal', locode: 'SN' },
    { name: 'Serbia', locode: 'RS' },
    { name: 'Seychelles', locode: 'SC' },
    { name: 'Sierra Leone', locode: 'SL' },
    { name: 'Singapore', locode: 'SG' },
    { name: 'Slovakia', locode: 'SK' },
    { name: 'Slovenia', locode: 'SI' },
    { name: 'Solomon Islands', locode: 'SB' },
    { name: 'Somalia', locode: 'SO' },
    { name: 'South Africa', locode: 'ZA' },
    { name: 'South Korea', locode: 'KR' },
    { name: 'South Sudan', locode: 'SS' },
    { name: 'Spain', locode: 'ES' },
    { name: 'Sri Lanka', locode: 'LK' },
    { name: 'Sudan', locode: 'SD' },
    { name: 'Suriname', locode: 'SR' },
    { name: 'Sweden', locode: 'SE' },
    { name: 'Switzerland', locode: 'CH' },
    { name: 'Syria', locode: 'SY' },
    { name: 'Taiwan', locode: 'TW' },
    { name: 'Tajikistan', locode: 'TJ' },
    { name: 'Tanzania', locode: 'TZ' },
    { name: 'Thailand', locode: 'TH' },
    { name: 'Timor-Leste', locode: 'TL' },
    { name: 'Togo', locode: 'TG' },
    { name: 'Tonga', locode: 'TO' },
    { name: 'Trinidad and Tobago', locode: 'TT' },
    { name: 'Tunisia', locode: 'TN' },
    { name: 'Turkey', locode: 'TR' },
    { name: 'Turkmenistan', locode: 'TM' },
    { name: 'Tuvalu', locode: 'TV' },
    { name: 'Uganda', locode: 'UG' },
    { name: 'Ukraine', locode: 'UA' },
    { name: 'United Arab Emirates', locode: 'AE' },
    { name: 'United Kingdom', locode: 'GB' },
    { name: 'United States', locode: 'US' },
    { name: 'Uruguay', locode: 'UY' },
    { name: 'Uzbekistan', locode: 'UZ' },
    { name: 'Vanuatu', locode: 'VU' },
    { name: 'Vatican City', locode: 'VA' },
    { name: 'Venezuela', locode: 'VE' },
    { name: 'Vietnam', locode: 'VN' },
    { name: 'Yemen', locode: 'YE' },
    { name: 'Zambia', locode: 'ZM' },
    { name: 'Zimbabwe', locode: 'ZW' }
  ];
  strings: any = {
    english: {
      select_country: 'Select country'
    },
    italian: {
      select_country: 'Seleziona il paese'
    }
  };
  searchText = '';
  selectedLabel = '';
  dropdownOpen = false;
  filteredCountries = [...this.countries];

  constructor(private _eref: ElementRef) { }

  ngOnInit() {
    if (this.control) {
      const currentValue = this.control.value;
      const found = this.countries.find(c => c.locode === currentValue);
      if (found) {
        this.selectedLabel = found.name;
      }

      this.control.valueChanges.subscribe(value => {
        const match = this.countries.find(c => c.locode === value);
        if (match) this.selectedLabel = match.name;
      });
    }
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.searchText = '';
      this.filteredCountries = [...this.countries];
    }
  }

  filterCountries() {
    const term = this.searchText.toLowerCase();
    this.filteredCountries = this.countries.filter(c => {
      const locode = c.locode ? c.locode.toString().toLowerCase() : '';
      const name = c.name ? c.name.toLowerCase() : '';
      return name.includes(term) || locode.includes(term);
    });
  }

  selectCountry(country: { name: string; locode: string }) {
    this.selectedLabel = country.name;
    this.dropdownOpen = false;
    if (this.control) {
      this.control.setValue(country.locode);
      this.control.markAsTouched();
    }
    this.change.emit(country.locode);
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent) {
    if (!this._eref.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }
}
